import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { fetchEntityFilterOptions } from '@/composables/api/useFilterOptions';
import { fetchRawDdexParties } from '@/composables/api/useRawDdexParties';
import { fetchTagCatalog, tagKind, type CatalogTag, type TagKind } from '@/composables/api/useTagCatalog';
import { TRACK_MATCH_STATUS_FILTER_OPTIONS } from '@/constants/trackMatchStatus';
import { TRACK_MATCH_TYPES } from '@/trackMatching/trackMatchingTypes';
import { STATIC_LOOKUP_OPTIONS } from '@/constants/lookupFilterOptions';
import {
  AUDIO_FILE_ISSUE_SEVERITY_OPTIONS,
  AUDIO_FILE_ISSUE_TYPE_OPTIONS,
  ORIGINAL_BITRATE_STATUS_OPTIONS,
  TRACK_RATING_OPTIONS,
} from '@/constants/audioFileIssueEnums';

/**
 * Every slug whose options ship with the frontend, and the options themselves.
 *
 * One map rather than a slug list plus a parallel if-ladder, because those two had
 * to be edited together by hand and nothing checked that they agreed. Registering a
 * slug in only one of them produced a dropdown that renders "No options found" with
 * no spinner and no error, which reads as "this field has no values" instead of
 * "nobody finished the wiring". `staticFilterOptions.test.ts` now walks the registry
 * and fails on any slug this map does not answer.
 */
/**
 * The curated lists, kept separate from the lookup tables so the two cannot
 * quietly swap places. The if-ladder this replaced tested these names BEFORE
 * falling back to the lookup tables, and spreading the tables last would have
 * reversed that without anything failing. They are spread first below, so a
 * curated name still wins, and `staticFilterOptions.test.ts` fails if a slug is
 * ever defined in both places.
 */
export const CURATED_OPTION_SOURCES: Readonly<Record<string, readonly unknown[]>> = Object.freeze({
  track_match_types: Object.values(TRACK_MATCH_TYPES),
  track_match_status: TRACK_MATCH_STATUS_FILTER_OPTIONS,
  audio_file_issue_severity: AUDIO_FILE_ISSUE_SEVERITY_OPTIONS,
  audio_file_issue_type: AUDIO_FILE_ISSUE_TYPE_OPTIONS,
  original_bitrate_status: ORIGINAL_BITRATE_STATUS_OPTIONS,
  track_rating: TRACK_RATING_OPTIONS,
});

const STATIC_OPTION_SOURCES: Readonly<Record<string, readonly unknown[]>> = Object.freeze({
  ...STATIC_LOOKUP_OPTIONS,
  ...CURATED_OPTION_SOURCES,
});

/** Bundled options for a slug, or an empty list when the slug is fetched or unknown. */
export function staticOptionsForSlug(slug: string | null | undefined): unknown[] {
  if (slug == null) return [];
  const options = STATIC_OPTION_SOURCES[slug];
  return options ? [...options] : [];
}

/** Slugs whose options are bundled with the frontend (no fetch required). */
const STATIC_SLUGS: readonly string[] = Object.keys(STATIC_OPTION_SOURCES);

/** Slugs that require a network request. Static slugs (e.g. track_match_types) are not listed. */
export const API_SLUGS = ['raw_ddex_parties', 'accounts', 'tags', 'live_track_tags'] as const;

/**
 * Option shape served by the tag slugs. `groupName` is the header/group tag's
 * name (the tag's parent_tag_id resolved through the catalog); root tags have
 * none. `kind` drives the option's font color (track/artist/user).
 */
export interface TagFilterOptionShape {
  id: number;
  name: string;
  groupName: string | null;
  kind: TagKind;
  isHeader: boolean;
}

/**
 * The tag catalog scoped for one entity family and ordered by group: Live
 * Tracks filter on track tags (track_tag = 1), every other tagged entity on
 * non-track tags (track_tag = 0). Groups sort by their header tag's name with
 * the header first, then its children alphabetically, so the dropdown reads
 * as group blocks with the group name on every row.
 */
export function buildTagOptions(catalog: CatalogTag[], trackTags: boolean): TagFilterOptionShape[] {
  const byId = new Map(catalog.map((tag) => [tag.id, tag] as const));
  return catalog
    .filter((tag) => tag.track_tag === trackTags)
    .map((tag) => {
      const parent = tag.parent_tag_id != null ? byId.get(tag.parent_tag_id) : undefined;
      return {
        id: tag.id,
        name: tag.name,
        groupName: parent?.name ?? null,
        kind: tagKind(tag),
        isHeader: tag.is_header_tag,
      };
    })
    .sort((a, b) => {
      const groupA = (a.groupName ?? a.name).toLowerCase();
      const groupB = (b.groupName ?? b.name).toLowerCase();
      if (groupA !== groupB) return groupA.localeCompare(groupB);
      // Within a group: the header tag itself leads, children follow by name.
      if (a.isHeader !== b.isHeader) return a.isHeader ? -1 : 1;
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
}

/** Options-source slug -> backend searchable-resource slug (POST /api/{slug}/search). */
const SEARCHABLE_ENTITY_API_SLUGS: Record<string, string> = {
  accounts: 'account',
};

function isApiSlug(slug: string | null | undefined): slug is (typeof API_SLUGS)[number] {
  return slug != null && (API_SLUGS as readonly string[]).includes(slug);
}

async function fetchOptionsForSlug(slug: string): Promise<unknown[]> {
  if (slug === 'raw_ddex_parties') {
    return fetchRawDdexParties();
  }
  if (slug === 'tags' || slug === 'live_track_tags') {
    return buildTagOptions(await fetchTagCatalog(), slug === 'live_track_tags');
  }
  if (slug in SEARCHABLE_ENTITY_API_SLUGS) {
    // Shared fetcher (capped at 1000 options; catalogs larger than that need
    // server-side combobox search — flagged follow-up).
    return fetchEntityFilterOptions(SEARCHABLE_ENTITY_API_SLUGS[slug]);
  }
  return [];
}

/**
 * Returns options and loading state for a given filter control options source slug.
 * Uses a single useQuery keyed by slug so only the active slug is fetched; adding
 * new API-backed slugs only adds a branch in fetchOptionsForSlug and in API_SLUGS.
 */
export function useFilterOptions(slug: ComputedRef<string | null | undefined>) {
  const currentSlug = computed(() => slug.value ?? null);

  const queryKey = computed(() => ['filterOptions', currentSlug.value] as const);

  const query = useQuery({
    queryKey,
    queryFn: ({ queryKey: [, slugFromKey] }) => fetchOptionsForSlug(slugFromKey ?? ''),
    enabled: computed(() => isApiSlug(currentSlug.value)),
    retry: 0,
  });

  const data = computed(() => {
    const activeSlug = currentSlug.value;
    if (activeSlug == null) return [];
    const staticOptions = staticOptionsForSlug(activeSlug);
    if (staticOptions.length > 0) return staticOptions;
    if (isApiSlug(activeSlug)) return query.data.value ?? [];
    return [];
  });

  function isStaticSlug(slug: string | null | undefined): boolean {
    return slug != null && STATIC_SLUGS.includes(slug);
  }

  /** True until the first fetch settles with data (or error). Covers pending before isFetching flips and avoids enabling the combobox with an empty list. */
  const isLoading = computed(() => {
    const activeSlug = currentSlug.value;
    if (activeSlug == null || isStaticSlug(activeSlug)) {
      return false;
    }
    return isApiSlug(activeSlug) && query.isLoading.value;
  });

  return { data, isLoading };
}
